import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "@/hooks/use-user";
import websocketService from "@/services/websocketService";

// Add a helper to debounce function calls
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const useUnreadMessages = () => {
  const { user } = useUser();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const MAX_FAILED_ATTEMPTS = 3; // Stop retrying after 3 failed attempts
  const isMounted = useRef(true);
  const isCurrentlyFetching = useRef(false);

  // Create a memoized fetchUnreadCount function that won't change on re-renders
  const fetchUnreadCount = useCallback(async () => {
    // Prevent concurrent fetches and check if component is still mounted
    if (isCurrentlyFetching.current || !isMounted.current) return;

    // Skip the API call if the user is not authenticated
    if (!user) {
      setLoading(false);
      setUnreadCount(0);
      return;
    }

    try {
      isCurrentlyFetching.current = true;
      setLoading(true);
      const response = await fetch("/api/messages/unread", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch unread count: ${response.statusText}`);
      }

      const data = await response.json();
      if (isMounted.current) {
        setUnreadCount(data.count);
        setError(null);
        setFailedAttempts(0); // Reset failed attempts on success
      }
    } catch (err) {
      console.error("Error fetching unread message count:", err);
      if (isMounted.current) {
        setFailedAttempts((prev) => prev + 1);
        setError("Failed to fetch unread messages");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      isCurrentlyFetching.current = false;
    }
  }, [user]);

  useEffect(() => {
    // Set up cleanup function to prevent state updates after unmount
    isMounted.current = true;

    // Skip setup if user is not authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    // Don't fetch if we've exceeded maximum retry attempts
    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      console.log(
        "Maximum retry attempts reached for unread messages. Stopping retries."
      );
      return;
    }

    // Initial fetch to get the initial count
    fetchUnreadCount();

    // Subscribe to WebSocket unread count updates
    const unsubscribe = websocketService.subscribeToUnreadCount((event) => {
      if (isMounted.current) {
        setUnreadCount(event.count);
        setLoading(false);
        setError(null);
      }
    });

    // Request an update via WebSocket (will be delivered when connection is ready)
    websocketService.requestUnreadCount();

    return () => {
      isMounted.current = false;
      // Unsubscribe from WebSocket updates
      unsubscribe();
    };
  }, [fetchUnreadCount, failedAttempts, user]);

  return {
    unreadCount,
    loading,
    error,
    refetch: fetchUnreadCount,
  };
};
